import { InfoCircleFilled, MoreOutlined } from '@ant-design/icons';
import { Button, Dropdown, Popover } from 'antd';
import type { Ref } from 'react';
import TrashIcon from 'assets/svg/TrashIcon';
import { colorConstants } from 'config/colorConstants';
import type { SimulationToolbarAction } from 'types/ppaSimulationMeta';
import { MORE_ACTIONS_DROPDOWN_BOX_SHADOW } from 'components/pages/PriceSimulatorV3ProjectScenarios.styles';
import {
    PPA_SIM_DELETE_CANCEL_BTN,
    PPA_SIM_DELETE_CONFIRM,
    PPA_SIM_DELETE_CONFIRM_BTN,
    PPA_SIM_DUPLICATE_CONFIRM,
    PPA_SIM_DUPLICATE_CONFIRM_NO_BTN,
    PPA_SIM_DUPLICATE_CONFIRM_YES_BTN,
    PPA_SIM_MORE_ACTIONS_BTN,
} from 'constants/ppaTestIds';
import type { SimulationToolbarProps } from './SimulationToolbar';

const CONFIRM_POPOVER_STYLE = {
    display: 'flex',
    width: 224,
    padding: 12,
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: 8,
    borderRadius: 8,
    background: colorConstants.white,
    // @code-guard: skip-next-line reason="inline popover shadow — no design token exists for this elevation level"
    boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
};

const CANCEL_BUTTON_STYLE = {
    height: 32,
    padding: '4px 15px',
    borderRadius: 6,
    border: `1px solid ${colorConstants.toolbarBorderGrey}`,
    background: colorConstants.white,
    color: colorConstants.blackTransparent88,
};

const PRIMARY_BUTTON_STYLE = {
    height: 32,
    padding: '4px 15px',
    borderRadius: 6,
    border: 'none',
    background: colorConstants.purpleFont,
    color: colorConstants.white,
};

const DELETE_BUTTON_STYLE = {
    height: 32,
    padding: '4px 15px',
    borderRadius: 6,
    border: 'none',
    background: colorConstants.growthDown,
    color: colorConstants.white,
};

function renderMoreActionsConfirm(props: SimulationToolbarProps): JSX.Element | null {
    if (props.moreActions.confirm === 'delete') {
        return (
            <div className="flex w-full flex-col items-end gap-2 self-stretch" data-testid={PPA_SIM_DELETE_CONFIRM}>
                <div className="flex w-full items-start gap-2 self-stretch">
                    <div className="flex h-[22px] shrink-0 items-start pt-[3px]">
                        <TrashIcon fill={colorConstants.growthDown} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-sm font-semibold leading-[22px]" style={{ color: colorConstants.blackTransparent88 }}>
                            Delete Scenario?
                        </span>
                        <p className="text-sm font-normal leading-[22px]" style={{ color: colorConstants.blackTransparent88 }}>
                            This action will delete this scenario permanently
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button style={CANCEL_BUTTON_STYLE} data-testid={PPA_SIM_DELETE_CANCEL_BTN} onClick={props.moreActions.onCloseConfirm}>Cancel</Button>
                    <Button style={DELETE_BUTTON_STYLE} loading={props.moreActions.isDeleteSubmitting} data-testid={PPA_SIM_DELETE_CONFIRM_BTN} onClick={props.moreActions.onDelete}>
                        Delete
                    </Button>
                </div>
            </div>
        );
    }

    if (props.moreActions.confirm === 'duplicate') {
        return (
            <div className="flex w-full flex-col items-end gap-2 self-stretch" data-testid={PPA_SIM_DUPLICATE_CONFIRM}>
                <div className="flex w-full items-start gap-2 self-stretch">
                    <div className="flex h-[22px] shrink-0 items-start pt-[3px]">
                        <InfoCircleFilled style={{ color: colorConstants.purpleFont, fontSize: 16, flexShrink: 0 }} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-sm font-semibold leading-[22px]" style={{ color: colorConstants.blackTransparent88 }}>
                            Confirm
                        </span>
                        <p className="text-sm font-normal leading-[22px]" style={{ color: colorConstants.blackTransparent88 }}>
                            This will duplicate the current scenario, in the same project.
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button style={CANCEL_BUTTON_STYLE} data-testid={PPA_SIM_DUPLICATE_CONFIRM_NO_BTN} onClick={props.moreActions.onCloseConfirm}>No</Button>
                    <Button style={PRIMARY_BUTTON_STYLE} loading={props.moreActions.isDuplicateSubmitting} data-testid={PPA_SIM_DUPLICATE_CONFIRM_YES_BTN} onClick={props.moreActions.onDuplicate}>
                        Yes
                    </Button>
                </div>
            </div>
        );
    }

    return null;
}

export function MoreActionsToolbarControl({
    action,
    isDisabled,
    props,
}: {
    action: SimulationToolbarAction;
    isDisabled: boolean;
    props: SimulationToolbarProps;
}): JSX.Element {
    const { moreActions } = props;
    const confirmContent = moreActions.confirm ? renderMoreActionsConfirm(props) : null;
    const isMenuActive = moreActions.isOpen || Boolean(moreActions.confirm);

    const trigger = (
        <Dropdown
            open={moreActions.isOpen}
            onOpenChange={moreActions.onOpenChange}
            trigger={[]}
            overlayClassName="more-actions-dropdown"
            menu={{
                triggerSubMenuAction: 'click',
                expandIcon: () => null,
                items: moreActions.items,
                onClick: moreActions.onMenuAction,
            }}
            overlayStyle={{
                borderRadius: '8px',
                background: colorConstants.white,
                boxShadow: MORE_ACTIONS_DROPDOWN_BOX_SHADOW,
            }}
        >
            <Button
                ref={moreActions.buttonRef as Ref<HTMLButtonElement>}
                className="toolbar-icon-btn"
                style={
                    isMenuActive
                        ? { borderColor: colorConstants.purpleFontPressed, color: colorConstants.purpleFontPressed }
                        : undefined
                }
                icon={<MoreOutlined />}
                onClick={moreActions.onButtonClick}
                aria-label={action.label}
                disabled={isDisabled}
                data-testid={PPA_SIM_MORE_ACTIONS_BTN}
            />
        </Dropdown>
    );

    if (!confirmContent) {
        return trigger;
    }

    return (
        <Popover
            open
            onOpenChange={(open) => {
                if (!open) {
                    moreActions.onCloseConfirm();
                }
            }}
            destroyTooltipOnHide
            trigger={[]}
            placement="bottomRight"
            transitionName=""
            overlayClassName="more-actions-confirm-popover popover-padding-remove"
            overlayInnerStyle={{ ...CONFIRM_POPOVER_STYLE, width: 224 }}
            content={confirmContent}
        >
            {trigger}
        </Popover>
    );
}
